<script lang="ts">
  import { onMount } from 'svelte';
  import { getServiceWorkerManager } from '../lib/service-worker-manager';

  let notificationPermission = $state<NotificationPermission>('default');
  let isSubscribed = $state(false);
  let isLoading = $state(false);
  let error = $state<string | null>(null);

  const swManager = getServiceWorkerManager();

  onMount(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      notificationPermission = Notification.permission;
      checkSubscription();
    }
  });

  async function checkSubscription() {
    try {
      const subscription = await swManager.getPushSubscription();
      isSubscribed = subscription !== null;
    } catch (err) {
      console.error('Failed to check subscription:', err);
    }
  }

  async function requestPermission() {
    isLoading = true;
    error = null;

    try {
      const permission = await swManager.requestNotificationPermission();
      notificationPermission = permission;

      if (permission === 'granted') {
        // Show a test notification
        await swManager.showNotification('Notifications Enabled', {
          body: 'You will now receive updates about your books and reading progress.',
          icon: '/icon-192.png',
        });
      } else if (permission === 'denied') {
        error = 'Notification permission denied. Please enable it in your browser settings.';
      }
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to request permission';
      console.error('Notification permission error:', err);
    } finally {
      isLoading = false;
    }
  }

  async function subscribe() {
    isLoading = true;
    error = null;

    try {
      // Note: You would need to provide your own VAPID public key here
      // For now, we'll just check permission
      const permission = await swManager.requestNotificationPermission();
      if (permission === 'granted') {
        // In a real implementation, you would subscribe to push notifications here
        // const subscription = await swManager.subscribeToPush(VAPID_PUBLIC_KEY);
        // Then send the subscription to your backend
        isSubscribed = true;
        await swManager.showNotification('Subscribed to Updates', {
          body: 'You will receive notifications about new books and updates.',
        });
      }
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to subscribe';
      console.error('Subscription error:', err);
    } finally {
      isLoading = false;
    }
  }

  async function unsubscribe() {
    isLoading = true;
    error = null;

    try {
      const success = await swManager.unsubscribeFromPush();
      if (success) {
        isSubscribed = false;
      }
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to unsubscribe';
      console.error('Unsubscription error:', err);
    } finally {
      isLoading = false;
    }
  }
</script>

<div class="notification-manager p-4 space-y-4">
  <div class="flex items-center justify-between">
    <div>
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
        Notifications
      </h3>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Get updates about your books and reading progress
      </p>
    </div>
    
    {#if notificationPermission === 'default'}
      <button
        onclick={requestPermission}
        disabled={isLoading}
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? 'Requesting...' : 'Enable Notifications'}
      </button>
    {:else if notificationPermission === 'granted'}
      {#if isSubscribed}
        <button
          onclick={unsubscribe}
          disabled={isLoading}
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 
                 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Unsubscribing...' : 'Unsubscribe'}
        </button>
      {:else}
        <button
          onclick={subscribe}
          disabled={isLoading}
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 
                 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Subscribing...' : 'Subscribe to Updates'}
        </button>
      {/if}
    {:else}
      <div class="text-sm text-red-600 dark:text-red-400">
        Notifications blocked
      </div>
    {/if}
  </div>

  {#if error}
    <div class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 
                dark:border-red-800 rounded-lg">
      <p class="text-sm text-red-800 dark:text-red-200">{error}</p>
    </div>
  {/if}

  {#if notificationPermission === 'granted'}
    <div class="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 
                dark:border-green-800 rounded-lg">
      <p class="text-sm text-green-800 dark:text-green-200">
        ✓ Notifications are enabled
      </p>
    </div>
  {:else if notificationPermission === 'denied'}
    <div class="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 
                dark:border-yellow-800 rounded-lg">
      <p class="text-sm text-yellow-800 dark:text-yellow-200">
        Notifications are blocked. Please enable them in your browser settings.
      </p>
    </div>
  {/if}
</div>

<style>
  .notification-manager {
    max-width: 100%;
  }
</style>
